/**
 * projectSlice — CRUD de projetos e controle de projeto ativo (load, create, archive, delete, setActive)
 *
 * Consumidores: App, Dashboard, CreateProjectModal, useProject, Archive, +10 páginas (via driveProjectStore)
 * Dependências: googleDrive, outlineStore, destinoStore, helpers
 * Persistência: Google Drive (pastas de projetos no Drive do usuário)
 */
import { googleDrive } from '../../services/googleDrive';
import { useOutlineStore } from '../outlineStore';
import { useDestinoStore } from '../destinoStore';
import { ensureAuthenticated } from './helpers';
import { logger } from '../../utils/logger';
import { safeSetItem } from '../../utils/safeLocalStorage';
import { bigwaToast } from '../../utils/bigwaToast';
import { auditLog } from '../../utils/auditLog';
import { trackPWAEngagement } from '../../hooks/usePWAEngagement';
// syncUserStats lazy-loaded to keep @supabase out of entry chunk (#35)
import type { DriveProject, ProjectOnboardingAnswers, SliceSet, SliceGet } from './types';
import { TEMPLATE_PAGE_LABELS, TEMPLATE_SIDEBAR_DIVIDERS } from './types';
import type { EmpiricalCodebook } from '../../types/empirical';

// Debounce flag to avoid multiple stats_sync calls per session
let _statsSynced = false;

export const createProjectSlice = (set: SliceSet, get: SliceGet) => ({
  loadProjects: async () => {
    // TTL check — skip reload if data is fresh (5 min)
    const _loadedAt = get().projectsLoadedAt;
    if (_loadedAt && Date.now() - _loadedAt < 300000) return;

    // Se já carregou e tem projetos, verificar se tem projeto ativo
    if (get().projectsLoaded && get().projects.length > 0) {
      // Garantir que há projeto ativo mesmo se já carregou
      const currentActiveId = get().activeProjectId;
      const activeProjects = (get().projects || []).filter(p => p.status === 'active');

      if (activeProjects.length > 0 && !currentActiveId) {
        get().setActiveProject(activeProjects[0].id);
      }
      return;
    }

    set({ error: null }); // v10: removido isLoading
    try {
      // ✅ Verificar autenticação ANTES de usar Drive
      await ensureAuthenticated();

      // Paralelizar: lista de projetos + metadata do projeto ativo
      const savedActiveId = localStorage.getItem('bigwa_activeProjectId');
      const [driveProjects, activeMetadata] = await Promise.all([
        googleDrive.listProjects(),
        savedActiveId
          ? googleDrive.readProjectJson(savedActiveId).catch(() => null)
          : Promise.resolve(null),
      ]);

      // Lazy loading: metadados completos só do projeto ativo
      const projects: DriveProject[] = driveProjects.map((p: { id: string; name: string; createdTime: string; trashed?: boolean }) => ({
        id: p.id,
        name: p.name.replace('[ARQUIVADO] ', ''),
        description: '',
        createdAt: p.createdTime,
        status: p.trashed ? 'trashed' : (p.name.startsWith('[ARQUIVADO]') ? 'archived' : 'active'),
        isActive: false,
        trashedAt: p.trashed ? new Date().toISOString() : undefined,
        daysRemaining: p.trashed ? 30 : undefined,
      }));

      if (savedActiveId && activeMetadata) {
        const idx = projects.findIndex(p => p.id === savedActiveId);
        if (idx !== -1) {
          const metadata = activeMetadata;
          projects[idx] = {
            ...projects[idx],
            description: metadata.description || '',
            createdAt: metadata.createdAt || projects[idx].createdAt,
            archivedAt: metadata.archivedAt,
            trashedAt: metadata.trashedAt,
            daysRemaining: metadata.trashedAt ? Math.ceil((new Date(metadata.trashedAt).getTime() + 30*24*60*60*1000 - Date.now()) / (24*60*60*1000)) : undefined,
            onboarding: metadata.onboarding,
            onboardingCompletedAt: metadata.onboardingCompletedAt,
            outline: metadata.outline,
            template: metadata.template || 'pesquisa',
            visiblePages: metadata.visiblePages || undefined,
            pageLabels: metadata.pageLabels || undefined,
            sidebarDividers: metadata.sidebarDividers !== undefined ? metadata.sidebarDividers : undefined,
          };

          if (metadata.outline) {
            set({ cachedOutline: metadata.outline, outlineLoaded: true });
            useOutlineStore.getState().loadFromProject(metadata.outline);
          }
        }
      }

      set({ projects, isLoading: false, projectsLoaded: true, projectsLoadedAt: Date.now() });

      // ✅ GARANTIR QUE SEMPRE HÁ 1 PROJETO ATIVO (OU LIMPAR SE NÃO HÁ)
      const activeProjects = (projects || []).filter(p => p.status === 'active');

      if (activeProjects.length > 0) {
        // Priorizar projeto salvo no localStorage (sobrevive a logout/login)
        if (savedActiveId && activeProjects.find(p => p.id === savedActiveId)) {
          get().setActiveProject(savedActiveId);
        } else {
          // Fallback: selecionar o primeiro ativo
          get().setActiveProject(activeProjects[0].id);
        }
      } else {
        // Nenhum projeto ativo, limpar seleção
        set({ activeProjectId: null });
        localStorage.removeItem('bigwa_activeProjectId');
      }

      // Carregar template de todos os projetos (para SpaceSwitcher mostrar ícones corretos)
      const activeId = get().activeProjectId;
      const otherActiveProjects = activeProjects.filter(p => p.id !== activeId);
      if (otherActiveProjects.length > 0) {
        Promise.all(otherActiveProjects.map(async (p) => {
          try {
            const meta = await googleDrive.readProjectJson(p.id);
            if (meta?.template) {
              set(state => ({
                projects: state.projects.map(proj =>
                  proj.id === p.id ? { ...proj, template: meta.template } : proj
                )
              }));
            }
          } catch {
            // Ignore — template will show default icon
          }
        }));
      }

      // Sync stats to Supabase (once per session, fire-and-forget)
      if (!_statsSynced) {
        _statsSynced = true;
        try {
          const userStr = localStorage.getItem('bigwa_user');
          if (userStr) {
            const userData = JSON.parse(userStr);
            if (userData?.email) {
              const activeCount = projects.filter(p => p.status === 'active').length;
              // Refs count will be 0 here since they load lazily; next stats_sync will have the real count
              import('../../lib/supabaseClient').then(m => m.syncUserStats(userData.email, 0, activeCount)).catch(() => {});
            }
          }
        } catch {
          // Ignore stats sync errors
        }
      }

    } catch (error: unknown) {
      logger.error('DriveProjectStore', 'Erro ao carregar projetos:', error);
      set({ error: error instanceof Error ? error.message : String(error), isLoading: false, projectsLoaded: true, projectsLoadedAt: Date.now() });
    }
  },

  // Invalida o cache de projetos (força recarregar na próxima vez)
  invalidateProjectsCache: () => {
    set({
      projects: [],
      projectsLoaded: false,
      projectsLoadedAt: null,
      cachedReferences: [],
      referencesLoaded: false,
      cachedResearchData: [],
      researchDataLoaded: false,
      cachedCrossings: [],
      crossingsLoaded: false,
      cachedOutline: null,
      outlineLoaded: false,
      cachedGroups: [],
      groupsLoaded: false,
      cachedCustomFields: [],
      customFieldsLoaded: false
    });
  },

  // Cria novo projeto
  createProject: async (name: string, description: string, template?: 'pesquisa' | 'leitura' | 'personalizado', visiblePages?: Record<string, boolean>, pageLabels?: Record<string, string>, sidebarDividers?: boolean | null) => {
    set({ isLoading: true, error: null });
    try {
      await ensureAuthenticated();
      const projectId = await googleDrive.createProject(name, description);

      const effectiveTemplate = template || 'pesquisa';
      const effectiveVisiblePages = (visiblePages as DriveProject['visiblePages']) || (template === 'leitura'
        ? { dadosPesquisa: false, conexoes: false, citacoes: false, colaboracao: false, arquivo: false }
        : { dadosPesquisa: true, conexoes: true, citacoes: true, colaboracao: true, arquivo: true });

      // Nomenclatura dinâmica: pageLabels e sidebarDividers por template
      const effectivePageLabels = pageLabels || TEMPLATE_PAGE_LABELS[effectiveTemplate] || {};
      const effectiveSidebarDividers = sidebarDividers !== undefined ? sidebarDividers : (TEMPLATE_SIDEBAR_DIVIDERS[effectiveTemplate] ?? true);

      // Persistir template, visiblePages, pageLabels e sidebarDividers no Google Drive
      await googleDrive.updateProject(projectId, {
        template: effectiveTemplate,
        visiblePages: effectiveVisiblePages,
        pageLabels: effectivePageLabels,
        sidebarDividers: effectiveSidebarDividers,
      });

      const newProject: DriveProject = {
        id: projectId,
        name,
        description,
        createdAt: new Date().toISOString(),
        status: 'active',
        isActive: false,
        template: effectiveTemplate,
        visiblePages: effectiveVisiblePages,
        pageLabels: effectivePageLabels,
        sidebarDividers: effectiveSidebarDividers,
      };

      set(state => ({
        projects: [...state.projects, newProject],
        isLoading: false
      }));

      // Sempre ativar o projeto recém-criado
      get().setActiveProject(projectId);
      auditLog({ operation: 'create', target: 'projeto', targetId: projectId, detail: `Projeto criado: ${name}`, success: true });
      trackPWAEngagement(1);
    } catch (error: unknown) {
      logger.error('DriveProjectStore', 'Erro ao criar projeto:', error);
      bigwaToast.notifyError('Não foi possível criar a mesa. Tente novamente.');
      set({ error: error instanceof Error ? error.message : String(error), isLoading: false });
      auditLog({ operation: 'create', target: 'projeto', detail: `Falha ao criar projeto: ${name}`, success: false, error: String(error) });
      throw error;
    }
  },

  // Atualiza projeto existente
  updateProject: async (id: string, updates: Partial<DriveProject>) => {
    const previousProjects = get().projects;
    set(state => ({
      projects: state.projects.map(p =>
        p.id === id ? { ...p, ...updates } : p
      ),
      isLoading: true, error: null
    }));
    try {
      await ensureAuthenticated();
      await googleDrive.updateProject(id, updates);
      set({ isLoading: false });
      auditLog({ operation: 'update', target: 'projeto', targetId: id, detail: `Projeto atualizado: ${Object.keys(updates).join(', ')}`, success: true });
    } catch (error: unknown) {
      set({ projects: previousProjects, error: error instanceof Error ? error.message : String(error), isLoading: false });
      logger.error('DriveProjectStore', 'Erro ao atualizar projeto:', error);
      bigwaToast.notifyError('Não foi possível atualizar a mesa. Alterações desfeitas.');
      auditLog({ operation: 'update', target: 'projeto', targetId: id, detail: `Falha ao atualizar projeto`, success: false, error: String(error) });
      throw error;
    }
  },

  // Fase Empírica v4 — Sprint 0.2 (DN8/DN13)
  // Atualiza apenas o empiricalCodebook do projeto.
  // Separado do updateProject genérico porque:
  // - googleDrive.updateProject tem allowlist fechado de 6 campos e faz silent
  //   drop de empiricalCodebook (verificado em projects.ts:398-447).
  // - googleDrive.updateProjectJson aceita Record<string, unknown> e é o caminho
  //   correto (verificado em projects.ts:452-486 — inclui withProjectLock + .bak
  //   + stampSchema + validateBeforeSave via saveProjectJson).
  // Replica o pattern Padrão 3 (optimistic + rollback) de updateProject acima.
  updateProjectCodebook: async (id: string, codebook: EmpiricalCodebook) => {
    const previousProjects = get().projects;
    set(state => ({
      projects: state.projects.map(p =>
        p.id === id ? { ...p, empiricalCodebook: codebook } : p
      ),
      isLoading: true, error: null
    }));
    try {
      await ensureAuthenticated();
      await googleDrive.updateProjectJson(id, { empiricalCodebook: codebook });
      set({ isLoading: false });
      auditLog({
        operation: 'update',
        target: 'projeto.empiricalCodebook',
        targetId: id,
        detail: `Codebook atualizado (columns: ${codebook.columns.length}, categories: ${codebook.categories.length})`,
        success: true,
      });
    } catch (error: unknown) {
      set({
        projects: previousProjects,
        error: error instanceof Error ? error.message : String(error),
        isLoading: false,
      });
      logger.error('DriveProjectStore', 'Erro ao atualizar empiricalCodebook:', error);
      bigwaToast.notifyError('Não foi possível atualizar o livro de códigos. Alterações desfeitas.');
      auditLog({
        operation: 'update',
        target: 'projeto.empiricalCodebook',
        targetId: id,
        detail: 'Falha ao atualizar empiricalCodebook',
        success: false,
        error: String(error),
      });
      throw error;
    }
  },

  // Arquiva projeto (move para Arquivo)
  archiveProject: async (id: string) => {
    const previousProjects = get().projects;
    const previousActiveProjectId = get().activeProjectId;
    const project = get().projects.find(p => p.id === id);
    if (!project) throw new Error('Projeto não encontrado');

    set(state => ({
      projects: state.projects.map(p =>
        p.id === id ? { ...p, status: 'archived' as const, archivedAt: new Date().toISOString() } : p
      ),
      isLoading: true, error: null
    }));

    // Se era o projeto ativo, selecionar outro
    let switchedTo: string | null = null;
    if (get().activeProjectId === id) {
      const nextActive = get().projects.find(p => p.status === 'active' && p.id !== id);
      if (nextActive) {
        get().setActiveProject(nextActive.id);
        switchedTo = nextActive.id;
      } else {
        set({ activeProjectId: null });
        localStorage.removeItem('bigwa_activeProjectId');
      }
    }

    try {
      await ensureAuthenticated();
      await googleDrive.archiveProject(id, project.name);
      set({ isLoading: false });
      auditLog({ operation: 'update', target: 'projeto', targetId: id, detail: `Projeto arquivado`, success: true });
      return switchedTo;
    } catch (error: unknown) {
      set({ projects: previousProjects, activeProjectId: previousActiveProjectId, error: error instanceof Error ? error.message : String(error), isLoading: false });
      logger.error('DriveProjectStore', 'Erro ao arquivar projeto:', error);
      bigwaToast.notifyError('Não foi possível arquivar a mesa. Alterações desfeitas.');
      auditLog({ operation: 'update', target: 'projeto', targetId: id, detail: `Falha ao arquivar projeto`, success: false, error: String(error) });
      throw error;
    }
  },

  // Move projeto para lixeira
  deleteProject: async (id: string) => {
    const previousProjects = get().projects;
    const previousActiveProjectId = get().activeProjectId;

    set(state => ({
      projects: state.projects.map(p =>
        p.id === id ? {
          ...p,
          status: 'trashed' as const,
          trashedAt: new Date().toISOString(),
          daysRemaining: 30
        } : p
      ),
      isLoading: true, error: null
    }));

    // Se era o projeto ativo, selecionar outro
    let switchedTo: string | null = null;
    if (get().activeProjectId === id) {
      const nextActive = get().projects.find(p => p.status === 'active' && p.id !== id);
      if (nextActive) {
        get().setActiveProject(nextActive.id);
        switchedTo = nextActive.id;
      } else {
        set({ activeProjectId: null });
        localStorage.removeItem('bigwa_activeProjectId');
      }
    }

    try {
      await ensureAuthenticated();
      await googleDrive.deleteProject(id);
      set({ isLoading: false });
      auditLog({ operation: 'delete', target: 'projeto', targetId: id, detail: `Projeto movido para lixeira`, success: true });
      return switchedTo;
    } catch (error: unknown) {
      set({ projects: previousProjects, activeProjectId: previousActiveProjectId, error: error instanceof Error ? error.message : String(error), isLoading: false });
      logger.error('DriveProjectStore', 'Erro ao mover projeto para lixeira:', error);
      bigwaToast.notifyError('Não foi possível mover a mesa para a lixeira. Alterações desfeitas.');
      auditLog({ operation: 'delete', target: 'projeto', targetId: id, detail: `Falha ao mover projeto para lixeira`, success: false, error: String(error) });
      throw error;
    }
  },

  // Restaura projeto da lixeira ou arquivo
  restoreProject: async (id: string) => {
    const previousProjects = get().projects;

    set(state => ({
      projects: state.projects.map(p =>
        p.id === id ? {
          ...p,
          status: 'active' as const,
          archivedAt: undefined,
          trashedAt: undefined,
          daysRemaining: undefined
        } : p
      ),
      isLoading: true, error: null
    }));

    try {
      await ensureAuthenticated();
      // restoreProject do googleDrive lida com ambos os casos (lixeira e arquivado)
      await googleDrive.restoreProject(id);
      set({ isLoading: false });
      auditLog({ operation: 'update', target: 'projeto', targetId: id, detail: `Projeto restaurado`, success: true });
    } catch (error: unknown) {
      set({ projects: previousProjects, error: error instanceof Error ? error.message : String(error), isLoading: false });
      logger.error('DriveProjectStore', 'Erro ao restaurar projeto:', error);
      bigwaToast.notifyError('Não foi possível restaurar a mesa. Alterações desfeitas.');
      auditLog({ operation: 'update', target: 'projeto', targetId: id, detail: `Falha ao restaurar projeto`, success: false, error: String(error) });
      throw error;
    }
  },

  // Exclui projeto permanentemente
  deletePermanently: async (id: string) => {
    const previousProjects = get().projects;

    set(state => ({
      projects: state.projects.filter(p => p.id !== id),
      isLoading: true, error: null
    }));

    try {
      await ensureAuthenticated();
      await googleDrive.deletePermanently(id);
      set({ isLoading: false });
      auditLog({ operation: 'delete', target: 'projeto', targetId: id, detail: `Projeto excluído permanentemente`, success: true });
    } catch (error: unknown) {
      set({ projects: previousProjects, error: error instanceof Error ? error.message : String(error), isLoading: false });
      logger.error('DriveProjectStore', 'Erro ao excluir projeto permanentemente:', error);
      bigwaToast.notifyError('Não foi possível excluir a mesa. Alterações desfeitas.');
      auditLog({ operation: 'delete', target: 'projeto', targetId: id, detail: `Falha ao excluir projeto permanentemente`, success: false, error: String(error) });
      throw error;
    }
  },

  // Define projeto ativo
  setActiveProject: (id: string) => {
    set({
      activeProjectId: id,
      // Limpa caches ao trocar de projeto
      cachedReferences: [],
      referencesLoaded: false,
      referencesLoadedAt: null,
      isLoadingReferences: false,
      cachedResearchData: [],
      researchDataLoaded: false,
      researchDataLoadedAt: null,
      isLoadingResearchData: false,
      cachedCrossings: [],
      crossingsLoaded: false,
      crossingsLoadedAt: null,
      cachedOutline: null,
      outlineLoaded: false,
      outlineLoadedAt: null,
      cachedGroups: [],
      groupsLoaded: false,
      groupsLoadedAt: null,
      cachedCustomFields: [],
      customFieldsLoaded: false,
      customFieldsLoadedAt: null,
    });
    safeSetItem('bigwa_activeProjectId', id);

    // Isolamento por-Mesa do destinoStore (Sprint 036): o Horizonte lê
    // destinos/assignments do destinoStore e não tem call site próprio de
    // loadFromDrive. reset() limpa a Mesa anterior na hora (sem flash);
    // loadFromDrive recarrega a nova (o TTL guard agora compara projectFolderId).
    useDestinoStore.getState().reset();
    void useDestinoStore.getState().loadFromDrive(id);

    // Carregar metadados completos do projeto ativo
    (async () => {
      try {
        await ensureAuthenticated();
        const metadata = await googleDrive.readProjectJson(id);

        set(state => ({
          projects: state.projects.map(p =>
            p.id === id ? {
              ...p,
              description: metadata.description || '',
              onboarding: metadata.onboarding,
              onboardingCompletedAt: metadata.onboardingCompletedAt,
              outline: metadata.outline,
              template: metadata.template || 'pesquisa',
              visiblePages: metadata.visiblePages || undefined,
              pageLabels: metadata.pageLabels || undefined,
              sidebarDividers: metadata.sidebarDividers !== undefined ? metadata.sidebarDividers : undefined,
            } : p
          )
        }));

        // Carregar outline se existir
        if (metadata.outline) {
          set({ cachedOutline: metadata.outline, outlineLoaded: true });
          useOutlineStore.getState().loadFromProject(metadata.outline);
        }
      } catch (error) {
        logger.error('DriveProjectStore', 'Erro ao carregar metadados do projeto ativo:', error);
        bigwaToast.notifyError('Não foi possível carregar os detalhes da mesa.');
      }
    })();
  },

  // Retorna projeto ativo
  getActiveProject: () => {
    const { projects, activeProjectId } = get();
    return projects.find(p => p.id === activeProjectId);
  },

  // Retorna projetos ativos
  getActiveProjects: () => {
    return get().projects.filter(p => p.status === 'active');
  },

  // Retorna projetos arquivados
  getArchivedProjects: () => {
    return get().projects.filter(p => p.status === 'archived');
  },

  // Retorna projetos na lixeira
  getTrashedProjects: () => {
    return get().projects.filter(p => p.status === 'trashed');
  },

  // === FUNÇÕES DE ONBOARDING ===

  // Verifica se o projeto tem onboarding preenchido
  hasProjectOnboarding: (projectId?: string) => {
    const id = projectId || get().activeProjectId;
    if (!id) return false;
    const project = get().projects.find(p => p.id === id);
    return !!project?.onboarding;
  },

  // Salva respostas do onboarding do projeto
  saveProjectOnboarding: async (answers: ProjectOnboardingAnswers) => {
    const activeProjectId = get().activeProjectId;
    if (!activeProjectId) {
      throw new Error('Nenhum projeto ativo selecionado');
    }

    try {
      await ensureAuthenticated();

      // Salvar no Drive
      await googleDrive.updateProjectOnboarding(activeProjectId, {
        onboarding: answers,
        onboardingCompletedAt: new Date().toISOString()
      });

      // Atualiza no estado local
      set(state => ({
        projects: state.projects.map(p =>
          p.id === activeProjectId
            ? { ...p, onboarding: answers, onboardingCompletedAt: new Date().toISOString() }
            : p
        )
      }));
      auditLog({ operation: 'update', target: 'projeto', targetId: activeProjectId, detail: `Onboarding salvo`, success: true });
    } catch (error) {
      logger.error('DriveProjectStore', 'Erro ao salvar onboarding:', error);
      bigwaToast.notifyError('Não foi possível salvar o onboarding. Tente novamente.');
      auditLog({ operation: 'update', target: 'projeto', targetId: activeProjectId, detail: `Falha ao salvar onboarding`, success: false, error: String(error) });
      throw error;
    }
  },

  // Retorna onboarding do projeto
  getProjectOnboarding: (projectId?: string) => {
    const id = projectId || get().activeProjectId;
    if (!id) return null;
    const project = get().projects.find(p => p.id === id);
    return project?.onboarding || null;
  },

  // Remove onboarding do projeto (reset do questionário)
  clearProjectOnboarding: async (projectId: string) => {
    try {
      await ensureAuthenticated();

      // Salvar no Drive com onboarding removido
      await googleDrive.updateProjectJson(projectId, {
        onboarding: null,
        onboardingCompletedAt: null,
      } as Record<string, unknown>);

      // Atualizar estado local
      set(state => ({
        projects: state.projects.map(p =>
          p.id === projectId
            ? { ...p, onboarding: undefined, onboardingCompletedAt: undefined }
            : p
        )
      }));

      auditLog({ operation: 'update', target: 'projeto', targetId: projectId, detail: 'Onboarding removido (reset questionário)', success: true });
    } catch (error) {
      logger.error('DriveProjectStore', 'Erro ao remover onboarding:', error);
      bigwaToast.notifyError('Não foi possível redefinir o questionário.');
      auditLog({ operation: 'update', target: 'projeto', targetId: projectId, detail: 'Falha ao remover onboarding', success: false, error: String(error) });
      throw error;
    }
  },
});
